import React, {useEffect, useRef, useState} from 'react';
import {config} from 'config';
import {isUuid} from 'helpers/isUuid';
import {isRestErrorWithStatus, useParamState} from 'hooks';
import useAuthState from 'hooks/useAuthState';
import {useBankIDStatus} from 'hooks/useBankIDStatus';
import {WrongBrowserError} from 'pages/error/WrongBrowserError';
import {getOrCreateBft} from 'services/bft';

export const isAllowedAuthorizeUrl = (
  url: string | null,
  allowedHost: string | undefined,
) => {
  if (!url || !allowedHost) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === allowedHost;
  } catch {
    return false;
  }
};

export const LoginNoBankIdPage = () => {
  const [{id}] = useParamState<{id?: string}>();
  const requestId = isUuid(id) ? id : undefined;
  const auth = useAuthState('NO');
  const entrypointTokenMutation = auth.getEntrypointAccessToken;
  const accessToken = entrypointTokenMutation.data?.accessToken;
  const [bft] = useState(() =>
    requestId ? getOrCreateBft(requestId).bft.value : undefined,
  );
  const [hasInvalidAuthorizeUrl, setHasInvalidAuthorizeUrl] = useState(false);
  const initCalledRef = useRef(false);

  const bankIDStatus = useBankIDStatus({
    token: requestId,
    authToken: accessToken,
    bft,
    statusEnabled: false,
  });
  const {initializeSessionAsync} = bankIDStatus;
  const initError = bankIDStatus.initializeSession.error;

  useEffect(() => {
    if (
      requestId &&
      !entrypointTokenMutation.data &&
      !entrypointTokenMutation.isPending &&
      !entrypointTokenMutation.isError
    ) {
      entrypointTokenMutation.mutate(undefined);
    }
  }, [entrypointTokenMutation, requestId]);

  useEffect(() => {
    if (!accessToken || !bft || initCalledRef.current) return;
    initCalledRef.current = true;

    initializeSessionAsync()
      .then(({authorizeUrl}) => {
        if (isAllowedAuthorizeUrl(authorizeUrl, config.bankIdNoAuthorizeHost)) {
          window.location.assign(authorizeUrl!);
        } else {
          setHasInvalidAuthorizeUrl(true);
        }
      })
      .catch(() => {
        // Surfaced through initializeSession.error
      });
  }, [accessToken, bft, initializeSessionAsync]);

  if (!requestId || isRestErrorWithStatus(initError, [400])) {
    return <WrongBrowserError />;
  }
  if (initError) throw initError;
  if (entrypointTokenMutation.error) throw entrypointTokenMutation.error;
  if (hasInvalidAuthorizeUrl) {
    throw new Error('BankID Norway authorize URL is missing or not allowed');
  }

  return null;
};
